import { Organization } from 'src/redux/validateSession/types';
import { COUNTRIES } from '../helpers/countries';
import { LOCAL_SETTINGS_KEY } from '../helpers/localStorageKeys.const';

const { REACT_APP_ENVIRONMENT, REACT_APP_BENCH_GOOGLE_TAG_MANAGER_KEY, REACT_APP_DASH_GOOGLE_TAG_MANAGER_KEY } =
  // @ts-ignore
  import.meta.env;

const isReviewEnv = REACT_APP_ENVIRONMENT === 'r';

export const APP_NAME = {
  bench: 'bench',
  dayforce_flex_work: 'dayforce_flex_work',
  maof: 'maof',
  ssa: 'ssa',
  network: 'network',
};

// Function to check if the hostname ends with a specific domain
function endsWithDomain(hostname: string, domain: string) {
  const domainPattern = new RegExp(`(^|\\.)${domain}\\.`);

  return domainPattern.test(hostname);
}

export function getOrganizationCodeByHostname(hostname: string) {
  // Checking for Maof domains
  if (endsWithDomain(hostname, 'getjob')) {
    return APP_NAME.maof;
  }

  // Checking for Bench domains
  if (endsWithDomain(hostname, 'compassbench')) {
    return APP_NAME.bench;
  }

  // Checking for SSA domains
  if ((hostname.startsWith('ssa.') || hostname.includes('.ssa.')) && endsWithDomain(hostname, 'jitjatjo')) {
    return APP_NAME.ssa;
  }

  if (REACT_APP_ENVIRONMENT === 'local' && hostname.startsWith('admin.')) {
    return APP_NAME.dayforce_flex_work;
  }

  const [orgDomain] = hostname.split('.');

  return orgDomain === 'dash' || orgDomain === 'admin' ? APP_NAME.dayforce_flex_work : orgDomain;
}

// Getting the hostname from the window location
const { hostname } = window.location;

function computeInitialOrgCode(): string {
  if (isReviewEnv) {
    const localSettings = localStorage.getItem(LOCAL_SETTINGS_KEY);
    const parsed = localSettings ? JSON.parse(localSettings) : null;

    return parsed?.organizationCode || APP_NAME.dayforce_flex_work;
  }

  return getOrganizationCodeByHostname(hostname) || APP_NAME.dayforce_flex_work;
}

let cachedOrganizationCode: string = computeInitialOrgCode();

type OrgCodeSourceStore = {
  subscribe: (listener: () => void) => () => void;
  getState: () => {
    FeatureFlags?: { flags?: { unified_login_enabled?: boolean } };
    ValidateSession?: { data?: { organizations?: Organization[] } };
  };
};

export function initOrgCodeSync(storeInstance: OrgCodeSourceStore): void {
  storeInstance.subscribe(() => {
    const state = storeInstance.getState();

    if (!state?.FeatureFlags?.flags?.unified_login_enabled) {
      return;
    }

    const activeOrg = state.ValidateSession?.data?.organizations?.find((org: Organization) => org.active);

    if (activeOrg?.internal_code && activeOrg.internal_code !== cachedOrganizationCode) {
      cachedOrganizationCode = activeOrg.internal_code;
    }
  });
}

export const getOrganizationCode = (): string => cachedOrganizationCode;

// Safe to call anywhere — including inside reducers — because they don't touch `store.getState()`.
export const isMaof = () => cachedOrganizationCode === APP_NAME.maof;
export const isBench = () => cachedOrganizationCode === APP_NAME.bench;
export const isDayforce = () => cachedOrganizationCode === APP_NAME.dayforce_flex_work;
export const isDayforceBenchMaofSpecific = () =>
  cachedOrganizationCode === APP_NAME.dayforce_flex_work ||
  cachedOrganizationCode === APP_NAME.bench ||
  cachedOrganizationCode === APP_NAME.maof;

export function getInitialCountryCode() {
  if (isMaof()) {
    return COUNTRIES.ISR.code;
  }

  return COUNTRIES.USA.code;
}

export function getTranslationsRootKey() {
  if (isBench()) {
    return APP_NAME.bench;
  }

  return 'dash';
}

export function getOrganizationGoogleTagManagerApiKey() {
  return isBench() ? REACT_APP_BENCH_GOOGLE_TAG_MANAGER_KEY : REACT_APP_DASH_GOOGLE_TAG_MANAGER_KEY;
}
